import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

const ClosureReport = ({ data, location }) => {
  const { t } = useTranslation();
  const {
    vatList,
    total,
    vat,
    base,
    count,
    currency,
    from,
    to,
    today,
    productCount,
    byPaymentMethod,
  } = data;
  // Per-payment-method summary — shown only when the result spans multiple methods.
  const PM_ORDER = ["card", "cash", "invoice", "web", "alreadyPaid", "none"];
  const paymentMethodKeys = byPaymentMethod
    ? Object.keys(byPaymentMethod).sort(
        (a, b) => PM_ORDER.indexOf(a) - PM_ORDER.indexOf(b)
      )
    : [];
  const showPaymentSummary = paymentMethodKeys.length > 1;
  let isSingleDay = false;
  if (from === to) {
    isSingleDay = true;
  }
  const { name, street, postcode, city, ico } = location;
  const rows = vatList.map((vatItem, index) => {
    const key = vatItem?.value;
    if (!vat.hasOwnProperty(key)) {
      return null;
    }
    return (
      <TableRow key={index}>
        <TableCell>{key} %</TableCell>
        <TableCell align="right">
          {base[key].toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right">
          {vat[key].toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right">
          {total[key].toFixed(2)} {currency}
        </TableCell>
      </TableRow>
    );
  });
  const totals = (
    <>
      <TableRow>
        <TableCell align="left" sx={{ fontWeight: "bold" }}>
          {t("pages.dsokReports.total")}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {base["overall"].toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {vat["overall"].toFixed(2)} {currency}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {total["overall"].toFixed(2)} {currency}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} sx={{ fontWeight: "bold" }}>
          {t("pages.dsokReports.receiptCount")}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {count} x
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left" colSpan={3} sx={{ fontWeight: "bold" }}>
          {t("pages.dsokReports.items")}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {productCount} x
        </TableCell>
      </TableRow>
    </>
  );
  const paymentSummary = showPaymentSummary ? (
    <>
      <TableRow>
        <TableCell colSpan={4} sx={{ fontWeight: "bold", pt: 2 }}>
          {t("pages.dsokReports.byPaymentMethod")}
        </TableCell>
      </TableRow>
      {paymentMethodKeys.map((m) => {
        const b = byPaymentMethod[m];
        return (
          <TableRow key={`pm-${m}`}>
            <TableCell align="left">
              {t(`pages.dsokReports.paymentMethods.${m}`, m)} ({b.count} x)
            </TableCell>
            <TableCell align="right">
              {b.base.toFixed(2)} {currency}
            </TableCell>
            <TableCell align="right">
              {b.vat.toFixed(2)} {currency}
            </TableCell>
            <TableCell align="right">
              {b.total.toFixed(2)} {currency}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  ) : null;
  const handlePrint = () => {
    const printContent = document.getElementById(
      "closure-report-table"
    ).outerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${t("pages.dsokReports.closure")}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              font-size: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th:first-child, td:first-child {
              text-align: left;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: right;
            }
            th {
              background-color: #f2f2f2;
              text-align: right;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            tr:hover {
              background-color: #f1f1f1;
            }
            .total-row {
              font-weight: bold;
              background-color: #e0e0e0;
            }
            .company-details {
              margin-bottom: 20px;
              text-align: left;
            }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between;">
            <div class="company-details">
              <h3>${name}</h3>
              <p>IČO: ${ico}, ${street} ${postcode} ${city}</p>
            </div>
            <div class="document-details">
              <h3>${t("pages.dsokReports.intervalClosure")}</h3>
              <p>${today}</p>
            </div>
          </div>
          <h2>${t("pages.dsokReports.informativeIntervalClosure")}</h2>
          ${
            isSingleDay
              ? `<p>${t("pages.dsokReports.date")} ${from}</p>`
              : `<p>${t("pages.dsokReports.from")} ${from} ${t(
                  "pages.dsokReports.to"
                )} ${to}</p>`
          }
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <Grid item md={12} xs={12}>
      <Box sx={{ display: "flex", justifyContent: "left", mt: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handlePrint}>
          {t("pages.dsokReports.print")}
        </Button>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "left", mt: 2, mb: 2 }}>
        <TableContainer
          component={Paper}
          sx={{ width: "100%" }}
          id="closure-report-table"
        >
          <Table sx={{ minWidth: { md: 650, xs: 0 } }} size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  {t("pages.dsokReports.vatRate")}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokReports.base")}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokReports.vat")}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokReports.total")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
            <TableBody>{totals}</TableBody>
            {paymentSummary && <TableBody>{paymentSummary}</TableBody>}
          </Table>
        </TableContainer>
      </Box>
    </Grid>
  );
};

export default ClosureReport;
